import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const KEY = "rinova_cart";
const lineKey = (i) => `${i.product_id}-${i.size || ""}-${i.color || ""}`;

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const mergedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  // On login: merge server cart with local
  useEffect(() => {
    if (!user) { mergedRef.current = false; return; }
    if (mergedRef.current) return;
    mergedRef.current = true;
    api.get("/cart").then((r) => {
      const server = r.data || [];
      setItems((local) => {
        const map = {};
        [...server, ...local].forEach((it) => {
          const k = lineKey(it);
          if (map[k]) map[k].quantity += it.quantity;
          else map[k] = { ...it };
        });
        return Object.values(map);
      });
    }).catch(() => {});
  }, [user]);

  // Persist to server when logged in
  useEffect(() => {
    if (user && mergedRef.current) {
      api.put("/cart", items).catch(() => {});
    }
  }, [items, user]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const k = lineKey(item);
      const existing = prev.find((p) => lineKey(p) === k);
      if (existing) return prev.map((p) => (lineKey(p) === k ? { ...p, quantity: p.quantity + item.quantity } : p));
      return [...prev, item];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((p) => lineKey(p) !== key)), []);
  const updateQty = useCallback((key, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((p) => (lineKey(p) === key ? { ...p, quantity: qty } : p)));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, subtotal, count, open, setOpen, lineKey }}>
      {children}
    </CartContext.Provider>
  );
}
